/* eslint-disable @typescript-eslint/no-explicit-any */

import React from "react";
import { useStandUp } from "../contexts/StandUpContext";
import { Check, X, Users, Loader2 } from "lucide-react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Invitation } from "../types";

const InvitationNotification: React.FC = () => {
  const { invitations, emailInvites, acceptInvitation, declineInvitation } =
    useStandUp();
  const [parent] = useAutoAnimate();
  const [processingId, setProcessingId] = React.useState<string | null>(null);

  // Deduplicate: If multiple invites exist for the same team, only show one
  const allPending = React.useMemo(() => {
    const merged = [...invitations, ...emailInvites];
    const uniqueTeams = new Map();

    merged.forEach((inv) => {
      // Prefer UID invitations over Email invitations if both exist
      const existing = uniqueTeams.get(inv.teamId);
      if (!existing || inv.inviteeId) {
        uniqueTeams.set(inv.teamId, inv);
      }
    });

    return Array.from(uniqueTeams.values());
  }, [invitations, emailInvites]);

  if (allPending.length === 0) return null;

  const handleAccept = async (inv: Invitation) => {
    setProcessingId(inv.id);
    try {
      await acceptInvitation(inv);
    } catch (e) {
      console.error(e);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (id: string) => {
    setProcessingId(id);
    try {
      await declineInvitation(id);
    } catch (e) {
      console.error(e);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div
      ref={parent}
      className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
    >
      {allPending.map((inv) => (
        <div
          key={inv.id}
          className="bg-white dark:bg-gray-800 border border-brand-200 dark:border-brand-900/50 rounded-2xl shadow-2xl p-4 pointer-events-auto flex flex-col gap-3 animate-in slide-in-from-right-10 duration-300"
        >
          <div className="flex gap-3">
            <div className="w-10 h-10 bg-brand-100 dark:bg-brand-900/30 rounded-full flex items-center justify-center text-brand-600 shrink-0">
              <Users size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                Team Invitation
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {inv.inviterName}
                </span>{" "}
                invited you to join{" "}
                <span className="font-semibold text-brand-600 dark:text-brand-400">
                  {inv.teamName}
                </span>
                .
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleAccept(inv)}
              disabled={!!processingId}
              className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-sm shadow-brand-500/20"
            >
              {processingId === inv.id ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Accept
            </button>
            <button
              onClick={() => handleDecline(inv.id)}
              disabled={!!processingId}
              className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-700 dark:text-gray-200 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95"
            >
              <X size={14} />
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default InvitationNotification;
